import type { MarketingFrame, MarketingLayout, MarketingViewport } from "../types";
import type { MarketingFramePort } from "../experience";
import { sampleWorkflow } from './workflowScore';
import { localize } from '../locale';

export const EDITORIAL_MAPS = [
  "converge",
  "extend",
  "align",
  "scrub-lock",
  "focus",
] as const;

export type EditorialMap = (typeof EDITORIAL_MAPS)[number];
export type EditorialSectionId = "workflow" | "audience" | "closer";

export interface WorkflowBeatDefinition {
  readonly map: EditorialMap;
  readonly copy: string;
}

export const WORKFLOW_BEATS: readonly WorkflowBeatDefinition[] = [
  { map: "converge", copy: "Создавай сцены в 3D\nобщаясь с AI" },
  { map: "extend", copy: "Работай там, где тебе удобно" },
  { map: "align", copy: "Опиши желаемую сцену" },
  { map: "scrub-lock", copy: "Проверь результат в Blender" },
  { map: "focus", copy: "Дорабатывай сцену до результата" },
] as const;

const workflowMarkup = String.raw`
<div class="marketing-inner editorial-workflow" data-editorial-section="workflow">
  <article class="editorial-beat editorial-beat--converge" data-editorial-map="converge">
    <h1 id="workflow-title" class="editorial-full-copy">Создавай сцены в 3D, общаясь с AI</h1>
    <p class="editorial-visual editorial-visual--intro" aria-hidden="true">
      <span data-editorial-fragment="creation">Создавай сцены в <b class="editorial-accent editorial-accent--3d">3D</b></span>
      <span data-editorial-fragment="dialogue">общаясь с <b class="editorial-accent editorial-accent--ai">AI</b></span>
    </p>
  </article>
  <article class="editorial-beat editorial-beat--extend" data-editorial-map="extend">
    <h2 class="editorial-full-copy">Работай там, где тебе удобно</h2>
    <p class="editorial-visual" aria-hidden="true"><span data-editorial-fragment="headline">Работай там, где тебе удобно</span></p>
  </article>
  <article class="editorial-beat editorial-beat--align" data-editorial-map="align">
    <h2 class="editorial-full-copy">Опиши желаемую сцену</h2>
    <p class="editorial-visual" aria-hidden="true"><span data-editorial-fragment="headline">Опиши желаемую сцену</span></p>
  </article>
  <article class="editorial-beat editorial-beat--scrub-lock" data-editorial-map="scrub-lock">
    <h2 class="editorial-full-copy">Проверь результат в Blender</h2>
    <p class="editorial-visual" aria-hidden="true"><span data-editorial-fragment="headline">Проверь результат в Blender</span></p>
  </article>
  <article class="editorial-beat editorial-beat--focus" data-editorial-map="focus">
    <h2 class="editorial-full-copy">Дорабатывай сцену до результата</h2>
    <p class="editorial-visual" aria-hidden="true"><span data-editorial-fragment="headline">Дорабатывай сцену до результата</span></p>
  </article>
</div>`;

const audienceMarkup = String.raw`
<div class="marketing-inner editorial-audience" data-editorial-section="audience">
  <article data-audience-side="novice">
    <h2 id="audience-title" class="editorial-full-copy">Даже если ты никогда не открывал Blender. И не собираешься его изучать. Начинай работать с 3D без огромной траты времени на обучение.</h2>
    <p class="editorial-visual" aria-hidden="true">
      <span data-editorial-fragment="experience">Даже если ты никогда не открывал Blender.</span>
      <span data-editorial-fragment="intent">И не собираешься его изучать.</span>
      <span data-editorial-fragment="outcome">Начинай работать с 3D без огромной траты времени на обучение.</span>
    </p>
  </article>
  <article data-audience-side="professional">
    <h2 class="editorial-full-copy">Сильная база сложной сцены — в разы быстрее. Собирай основу разговором и доводи её вручную до профессионального уровня.</h2>
    <p class="editorial-visual" aria-hidden="true">
      <span data-editorial-fragment="foundation">Сильная база сложной сцены — в разы быстрее.</span>
      <span data-editorial-fragment="finish">Собирай основу разговором и доводи её вручную до профессионального уровня.</span>
    </p>
  </article>
</div>`;

const closerMarkup = String.raw`
<div class="marketing-inner editorial-closer" data-editorial-section="closer" data-editorial-map="closer">
  <h2 id="closer-title" class="editorial-full-copy">Ты как никогда близок к миру 3D. Погрузись в мир 3D вместе с Blender Bridge.</h2>
  <p class="editorial-visual" aria-hidden="true">
    <span data-editorial-fragment="proximity">Ты как никогда близок к миру 3D.</span>
    <span data-editorial-fragment="invitation">Погрузись в мир 3D вместе с Blender Bridge.</span>
  </p>
</div>`;

export const EDITORIAL_SECTION_MARKUP: Readonly<Record<EditorialSectionId, string>> = Object.freeze({
  workflow: workflowMarkup,
  audience: audienceMarkup,
  closer: closerMarkup,
});

export interface EditorialBeatSnapshot {
  readonly map: EditorialMap;
  readonly progress: number;
  readonly assembly: number;
  readonly presence: number;
  readonly pin: number;
  readonly lock: number;
  readonly phase: "before" | "active" | "after";
  readonly fragments: readonly number[];
}

export interface EditorialSnapshot {
  readonly state: "managed";
  readonly activeMap: EditorialMap;
  readonly workflow: readonly EditorialBeatSnapshot[];
  readonly audience: {
    readonly novice: number;
    readonly professional: number;
  };
  readonly closer: number;
}

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const easeOut = (value: number): number => 1 - (1 - clamp01(value)) ** 3;
const smoothstep = (value: number): number => {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
};
const range = (progress: number, start: number, end: number): number =>
  clamp01((progress - start) / Math.max(0.0001, end - start));

/** Уверенное прибытие: быстро, потом торможение. Для строк, которые встают на место. */
const settle = (progress: number, start: number, end: number): number =>
  easeOut(range(progress, start, end));

/** Ход с плавным началом и концом. Для движения, которое зритель должен успеть увидеть. */
const glide = (progress: number, start: number, end: number): number =>
  smoothstep(range(progress, start, end));

/*
 * Каждая фраза обязана быть собранной и прочитанной. Внутреннее движение такта
 * заканчивается к ASSEMBLED_BY, дальше идёт удержание до HOLD_END. Без этого
 * удержания предложение целиком существует только в кадре, которого зритель
 * никогда не видит: раньше «процесс.» доходил до полной яркости ровно в тот
 * момент, когда такт уже уходил.
 */
const ASSEMBLED_BY = 0.52;
const PRESENCE_IN_START = 0.015;
const PRESENCE_IN_END = 0.13;
const HOLD_END = 0.87;
const PRESENCE_OUT_END = 0.99;

const beatPresence = (progress: number): number =>
  smoothstep(range(progress, PRESENCE_IN_START, PRESENCE_IN_END))
  * (1 - smoothstep(range(progress, HOLD_END, PRESENCE_OUT_END)));

const beatFragments = (map: EditorialMap, progress: number): readonly number[] => {
  switch (map) {
    case "converge": return [glide(progress, 0.04, 0.4), glide(progress, 0.1, 0.46)];
    case "extend":
    case "align":
    case "scrub-lock":
    case "focus": return [settle(progress, 0.04, 0.38)];
  }
};

const beatAssembly = (map: EditorialMap, progress: number): number =>
  map === "converge" ? settle(progress, 0.02, 0.48) : settle(progress, 0.04, 0.38);

const beatLock = (map: EditorialMap, progress: number): number =>
  map === "scrub-lock" ? smoothstep(range(progress, 0.28, 0.42)) : 0;

/* «шаг за шагом» — шаги, а не скольжение: у каждого слова свой короткий
   интервал прибытия, окна почти не перекрываются. */
const STEP_WORDS_START = 0.18;
const STEP_WORDS_END = ASSEMBLED_BY;

export function sampleStepWords(progress: number, wordCount: number): readonly number[] {
  if (wordCount <= 0) return [];
  const stride = (STEP_WORDS_END - STEP_WORDS_START) / wordCount;
  return Array.from({ length: wordCount }, (_, index) => {
    const start = STEP_WORDS_START + index * stride;
    return easeOut(range(progress, start, start + stride * 0.82));
  });
}

export function sampleWordReveal(progress: number, wordCount: number): readonly number[] {
  if (wordCount <= 0) return [];
  const reveal = range(progress, 0.16, 0.62);
  const head = reveal * (wordCount + 1.15);
  return Array.from({ length: wordCount }, (_, index) =>
    smoothstep(clamp01((head - index) / 2.15)));
}

export function sampleBeatWords(
  map: EditorialMap,
  progress: number,
  wordCount: number,
): readonly number[] {
  if (map === "extend") return sampleStepWords(progress, wordCount);
  if (map === "focus") return sampleWordReveal(progress, wordCount);
  return Array.from({ length: Math.max(0, wordCount) }, () => 1);
}

export function sampleEditorialFrame(frame: MarketingFrame): EditorialSnapshot {
  if (frame.motion === "reduced") {
    // Keep the physical chapter ownership/pinning. Only the decorative reveal
    // is resolved; exposing every chapter at once breaks the retained spans.
    const geometry = sampleEditorialFrame({ ...frame, motion: "full" });
    return {
      ...geometry,
      workflow: geometry.workflow.map((beat) => ({
        ...beat,
        assembly: 1,
        lock: beat.map === "scrub-lock" ? 1 : 0,
        fragments: beatFragments(beat.map, 1),
      })),
      audience: { novice: 1, professional: 1 },
      closer: 1,
    };
  }
  const workflowProgress = clamp01(frame.sections.workflow.progress);
  const whereProgress = clamp01(frame.sections.where.progress);
  const activeIndex = Math.min(WORKFLOW_BEATS.length - 1, Math.floor(workflowProgress * WORKFLOW_BEATS.length));
  const workflow = WORKFLOW_BEATS.map(({ map }, index): EditorialBeatSnapshot => {
    const rawProgress = workflowProgress * WORKFLOW_BEATS.length - index;
    const progress = clamp01(rawProgress);
    const isFinalHold = index === WORKFLOW_BEATS.length - 1 && workflowProgress >= 0.999;
    const finalReleased = isFinalHold && whereProgress >= 0.62;
    const presence = frame.handoff >= 0.999
      ? isFinalHold ? (finalReleased ? 0 : 1) : index === activeIndex ? 1 : 0
      : 0;
    const phase = frame.handoff < 0.999
      ? "before"
      : index < activeIndex || finalReleased
        ? "after"
        : index === activeIndex
          ? "active"
          : "before";
    return {
      map,
      progress,
      assembly: beatAssembly(map, progress),
      presence,
      pin: progress,
      lock: beatLock(map, progress),
      phase,
      fragments: beatFragments(map, progress),
    };
  });
  const audienceProgress = clamp01(frame.sections.audience.progress);
  return {
    state: "managed",
    activeMap: WORKFLOW_BEATS[activeIndex]?.map ?? "converge",
    workflow,
    audience: {
      novice: easeOut(audienceProgress / 0.3),
      professional: easeOut((audienceProgress - 0.38) / 0.3),
    },
    closer: easeOut(clamp01(frame.sections.closer.progress) / 0.36),
  };
}

const setNumber = (node: HTMLElement, property: string, value: number): void => {
  node.style.setProperty(property, value.toFixed(4));
};

interface FragmentMotion {
  readonly x: number;
  readonly y: number;
  readonly opacity: number;
}

/*
 * Смещения сдержанные: смысл несёт порядок и момент прибытия, а не амплитуда.
 * Нижний порог непрозрачности — уровень читаемого текста; приходящая фраза
 * должна выглядеть как приходящая, а не как несработавший рендер.
 */
const OFFSETS = {
  /* Вдвое тише прежнего: окна стали главными, текст не должен с ними спорить. */
  desktop: { converge: 28, extend: 8, align: 16 },
  mobile: { converge: 8, extend: 4, align: 6 },
} as const;

const arriving = (value: number): number => 0.28 + value * 0.72;
const fragmentMotion = (
  map: EditorialMap,
  fragmentIndex: number,
  fragmentProgress: number,
  mobile: boolean,
): FragmentMotion => {
  const offsets = mobile ? OFFSETS.mobile : OFFSETS.desktop;
  const inverse = 1 - fragmentProgress;
  switch (map) {
    case "converge":
      return {
        x: (fragmentIndex === 0 ? -1 : 1) * inverse * offsets.converge * 0.72,
        y: 0,
        opacity: arriving(fragmentProgress),
      };
    case "extend":
    case "align":
    case "scrub-lock":
    case "focus":
      return { x: 0, y: 0, opacity: arriving(fragmentProgress) };
  }
};

interface WordMotion {
  readonly x: number;
  readonly y: number;
  readonly opacity: number;
}

const wordMotion = (map: EditorialMap, value: number, mobile: boolean): WordMotion => {
  const offsets = mobile ? OFFSETS.mobile : OFFSETS.desktop;
  const inverse = 1 - value;
  if (map === "extend") {
    return { x: inverse * offsets.extend * 1.8, y: 0, opacity: 0.18 + value * 0.82 };
  }
  return { x: 0, y: inverse * 0.1, opacity: 0.18 + value * 0.82 };
};

export class MarketingEditorial implements MarketingFramePort {
  private readonly beats: readonly HTMLElement[];
  private readonly audience: readonly HTMLElement[];
  private readonly closer: HTMLElement | null;
  /* Шестая фраза живёт в секции where и ведётся её прогрессом, но по тем же
     правилам такта: короб равен своему отрезку скролла, фраза удерживается. */
  private readonly elsewhere: HTMLElement | null;
  /* Сцена с двумя окнами стоит в кадре все шесть тактов, поэтому её встречное
     смещение считается от объединённого отрезка workflow + where. */
  private readonly stage: HTMLElement | null;
  private layout: MarketingLayout | null = null;
  private readonly wordRevealRoots: readonly HTMLElement[];
  private readonly wordRevealOriginals = new Map<HTMLElement, readonly Node[]>();
  private readonly localeChange=():void=>{
    for(const [root,original]of this.wordRevealOriginals)root.replaceChildren(...original.map(node=>node.cloneNode(true)));
    localize(this.root);
    for(const root of this.wordRevealRoots)this.prepareWordReveal(root);
  };

  constructor(private readonly root: HTMLElement) {
    localize(root);
    this.beats = EDITORIAL_MAPS.map((map) => {
      const node = root.querySelector<HTMLElement>(`[data-editorial-map="${map}"]`);
      if (!node) throw new Error(`Missing editorial map: ${map}`);
      return node;
    });
    this.audience = ["novice", "professional"].map((side) => {
      const node = root.querySelector<HTMLElement>(`[data-audience-side="${side}"]`);
      if (!node) throw new Error(`Missing editorial audience side: ${side}`);
      return node;
    });
    const closer = root.querySelector<HTMLElement>("[data-editorial-map=\"closer\"]");
    this.closer = closer;
    this.elsewhere = root.querySelector<HTMLElement>('[data-editorial-map="elsewhere"]');
    this.stage = root.querySelector<HTMLElement>("[data-editorial-stage]");
    this.wordRevealRoots = Array.from(root.querySelectorAll<HTMLElement>("[data-editorial-word-reveal]"));
    for (const revealRoot of this.wordRevealRoots) this.prepareWordReveal(revealRoot);
    root.ownerDocument?.defaultView?.addEventListener('bb:localechange',this.localeChange);
    root.dataset.editorialMounted = "true";
  }

  private prepareWordReveal(revealRoot: HTMLElement): void {
    this.wordRevealOriginals.set(
      revealRoot,
      Array.from(revealRoot.childNodes, (node) => node.cloneNode(true)),
    );
    const document = revealRoot.ownerDocument;
    const walker = document.createTreeWalker(revealRoot, 4);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      const parent = node.parentElement;
      if (!node.nodeValue?.trim()) continue;
      if (parent?.closest("script,style,input,textarea,select,button,[data-no-split]")) continue;
      textNodes.push(node);
    }
    for (const node of textNodes) {
      const fragment = document.createDocumentFragment();
      const language=document.documentElement?.lang||'en';
      const tokens=typeof Intl.Segmenter==='function'?[...new Intl.Segmenter(language,{granularity:'word'}).segment(node.nodeValue??'')].map(part=>part.segment):node.nodeValue?.split(/(\s+)/)??[];
      for (const token of tokens) {
        if (!token) continue;
        if (/^\s+$/.test(token)) {
          fragment.append(document.createTextNode(token));
          continue;
        }
        const word = document.createElement("span");
        word.dataset.editorialWord = "";
        word.textContent = token;
        fragment.append(word);
      }
      node.replaceWith(fragment);
    }
  }

  render(frame: MarketingFrame, _now: number): EditorialSnapshot {
    const snapshot = sampleEditorialFrame(frame);
    const mobile = frame.viewport === "mobile";
    const visibleWorkflowBeat = snapshot.workflow.find((beat) => beat.phase === "active" && beat.presence > 0.01);
    if (visibleWorkflowBeat) this.root.dataset.editorialActiveMap = visibleWorkflowBeat.map;
    else delete this.root.dataset.editorialActiveMap;
    snapshot.workflow.forEach((beat, index) => {
      const node = this.beats[index];
      if (!node) return;
      setNumber(node, "--editorial-progress", beat.progress);
      setNumber(node, "--editorial-assembly", beat.assembly);
      setNumber(node, "--editorial-presence", beat.presence);
      setNumber(node, "--editorial-pin", beat.pin);
      setNumber(node, "--editorial-lock", beat.lock);
      node.dataset.editorialPhase = beat.phase;
      node.toggleAttribute("inert", beat.phase !== "active");
      node.setAttribute("aria-hidden", String(beat.phase !== "active"));
      const fragments = node.querySelectorAll<HTMLElement>("[data-editorial-fragment]");
      beat.fragments.forEach((value, fragmentIndex) => {
        const fragment = fragments[fragmentIndex];
        if (!fragment) return;
        setNumber(fragment, "--fragment-progress", value);
        const motion = fragmentMotion(beat.map, fragmentIndex, value, mobile);
        fragment.style.setProperty("--fragment-x", `${motion.x.toFixed(3)}px`);
        fragment.style.setProperty("--fragment-y", `${motion.y.toFixed(3)}px`);
        setNumber(fragment, "--fragment-opacity", motion.opacity);
      });
      for (const revealRoot of node.querySelectorAll<HTMLElement>("[data-editorial-word-reveal]")) {
        const words = Array.from(revealRoot.querySelectorAll<HTMLElement>("[data-editorial-word]"));
        sampleBeatWords(beat.map, beat.progress, words.length).forEach((value, wordIndex) => {
          const word = words[wordIndex];
          if (!word) return;
          const motion = wordMotion(beat.map, value, mobile);
          setNumber(word, "--word-progress", value);
          setNumber(word, "--word-opacity", motion.opacity);
          word.style.setProperty("--word-x", `${motion.x.toFixed(3)}px`);
          word.style.setProperty("--word-y", `${motion.y.toFixed(4)}em`);
        });
      }
    });
    this.renderElsewhere(frame);
    this.renderFinalHeadline(frame);
    this.renderStage(frame);
    const cinema=this.root.ownerDocument?.defaultView?.__BB_CINEMA__;
    if(cinema?.active&&['intro','workflow'].includes(cinema.chapter.id)) {
      const intro=cinema.chapter.id==='intro',shot=sampleWorkflow(cinema.state.elapsed);
      const current=intro?0:shot.index+1;
      this.beats.forEach((node,index)=>{
        const incoming=index===current;
        const outgoing=!intro&&shot.index>0&&index===current-1;
        const reveal=intro?smoothstep((cinema.state.elapsed-550)/1100):shot.reveal;
        setNumber(node,'--editorial-presence',incoming?reveal:outgoing?shot.previous:0);
        setNumber(node,'--editorial-reveal',incoming?reveal:1);
        if(intro&&incoming)node.style.setProperty('--editorial-pin',String(frame.sections.workflow.progress*5));
        node.dataset.editorialPhase=incoming?'active':index<current?'after':'before';
        node.setAttribute('aria-hidden',String(!incoming));node.toggleAttribute('inert',!incoming);
        // All four headings share a baseline while the physical document moves.
        if(!intro&&!cinema.transitioning)setNumber(node,'--editorial-pin',frame.sections.workflow.progress*5-index);
      });
      if(this.stage){setNumber(this.stage,'--editorial-stage-presence',intro?0:shot.windows);this.stage.dataset.editorialStageVisible=String(!intro&&shot.windows>0);}
    }
    const novice = this.audience[0];
    const professional = this.audience[1];
    if (!this.root.querySelector('#audience[data-narrative-directed="true"]')) {
      if (novice) setNumber(novice, "--audience-emphasis", snapshot.audience.novice);
      if (professional) setNumber(professional, "--audience-emphasis", snapshot.audience.professional);
    }
    if (this.closer) setNumber(this.closer, "--editorial-assembly", snapshot.closer);
    return snapshot;
  }

  resize(_viewport: MarketingViewport, layout?: MarketingLayout): void {
    if (layout) this.layout = layout;
  }

  /** Прогресс по объединённому отрезку workflow + where, 0…1 на шесть тактов. */
  private sequenceProgress(frame: MarketingFrame): number | null {
    const layout = this.layout;
    if (!layout) return null;
    const start = layout.sections.workflow.startPx;
    const end = layout.sections.where.endPx;
    return clamp01((frame.travelPx - start) / Math.max(1, end - start));
  }

  private renderElsewhere(frame: MarketingFrame): void {
    const node = this.elsewhere;
    if (!node) return;
    const sequence = this.sequenceProgress(frame);
    const progress = sequence === null ? clamp01(frame.sections.where.progress)
        : clamp01((sequence - 5 / 7) * 7);
    const presence = frame.motion === "reduced" ? Number(progress > 0 && progress < 1) : beatPresence(progress);
    setNumber(node, "--editorial-progress", progress);
    setNumber(node, "--editorial-assembly", frame.motion === "reduced" ? 1 : settle(progress, 0.02, ASSEMBLED_BY));
    setNumber(node, "--editorial-presence", presence);
    setNumber(node, "--editorial-pin", progress);
    const fragments = node.querySelectorAll<HTMLElement>("[data-editorial-fragment]");
    [settle(progress, 0.02, 0.36), settle(progress, 0.12, 0.46)].forEach((value, index) => {
      const fragment = fragments[index];
      if (!fragment) return;
      setNumber(fragment, "--fragment-progress", value);
      setNumber(fragment, "--fragment-opacity", arriving(value));
      fragment.style.setProperty("--fragment-x", "0px");
      fragment.style.setProperty("--fragment-y", `${((1 - value) * 14).toFixed(3)}px`);
    });
  }

  private renderFinalHeadline(frame: MarketingFrame): void {
    const node = this.beats.at(-1);
    const layout = this.layout;
    if (!node || !layout) {
      node?.style.removeProperty("--editorial-hold-y");
      return;
    }
    const workflow = layout.sections.workflow;
    const beatSpan = (workflow.endPx - workflow.startPx) / WORKFLOW_BEATS.length;
    const release = layout.sections.where.startPx + beatSpan;
    const hold = Math.max(0, Math.min(frame.travelPx - workflow.endPx, release - workflow.endPx));
    node.style.setProperty("--editorial-hold-y", `${hold.toFixed(2)}px`);
  }

  private renderStage(frame: MarketingFrame): void {
    const node = this.stage;
    const layout = this.layout;
    if (!node) return;
    if (!layout) {
      node.style.removeProperty("--editorial-stage-y");
      node.style.setProperty("--editorial-stage-presence", "0");
      node.dataset.editorialStageVisible = "false";
      return;
    }
    const start = layout.sections.workflow.startPx;
    const workflow = layout.sections.workflow;
    const beatSpan = (workflow.endPx - workflow.startPx) / WORKFLOW_BEATS.length;
    /* Отпускаем за один такт до конца секции where: дальше окна едут вверх
       обычной прокруткой, а не стоят до последнего и исчезают. */
    const release = layout.sections.where.startPx + beatSpan;
    const held = Math.max(0, Math.min(frame.travelPx - start, release - start));
    node.style.setProperty("--editorial-stage-y", `${held.toFixed(2)}px`);
    /*
     * Сцена принадлежит только своему отрезку. Без этого гашения абсолютно
     * позиционированные окна оставались нарисованными и наезжали на соседние
     * секции — и сверху, и снизу.
     */
    /* Первый такт принадлежит только большому титру. Две среды открываются
       уже под следующим заголовком и затем остаются одновременно в кадре. */
    const workflowProgress = clamp01(frame.sections.workflow.progress);
    /* На телефоне доли прокрутки нет: раздел показывается целиком и сразу,
       поэтому пара окон принадлежит самому экрану, а не его середине. */
    const stageVisible = frame.viewport === "mobile"
      ? frame.activeSection === "workflow" || frame.activeSection === "where"
      : frame.handoff >= 0.999 && workflowProgress >= 0.2;
    setNumber(node, "--editorial-stage-presence", stageVisible ? 1 : 0);
    node.dataset.editorialStageVisible = stageVisible ? "true" : "false";
  }

  destroy(): void {
    this.root.ownerDocument?.defaultView?.removeEventListener('bb:localechange',this.localeChange);
    delete this.root.dataset.editorialMounted;
    this.stage?.style.removeProperty("--editorial-stage-y");
    if (this.stage) delete this.stage.dataset.editorialStageVisible;
    for (const beat of this.beats) {
      beat.style.removeProperty("--editorial-hold-y");
      for (const property of [
        "--editorial-progress",
        "--editorial-assembly",
        "--editorial-presence",
        "--editorial-pin",
        "--editorial-lock",
      ]) beat.style.removeProperty(property);
      delete beat.dataset.editorialPhase;
      beat.removeAttribute("aria-hidden");
      beat.removeAttribute("inert");
      for (const fragment of beat.querySelectorAll<HTMLElement>("[data-editorial-fragment]")) {
        fragment.style.removeProperty("--fragment-progress");
        fragment.style.removeProperty("--fragment-x");
        fragment.style.removeProperty("--fragment-y");
        fragment.style.removeProperty("--fragment-opacity");
      }
    }
    for (const revealRoot of this.wordRevealRoots) {
      const original = this.wordRevealOriginals.get(revealRoot);
      if (original) revealRoot.replaceChildren(...original.map((node) => node.cloneNode(true)));
    }
    this.wordRevealOriginals.clear();
    delete this.root.dataset.editorialActiveMap;
    for (const side of this.audience) side.style.removeProperty("--audience-emphasis");
    this.closer?.style.removeProperty("--editorial-assembly");
  }
}

export const createMarketingEditorial = (root: HTMLElement): MarketingEditorial =>
  new MarketingEditorial(root);
